// QR code (D-111) : le QR de la plaquette et de l'affiche, vers la page partenaires du club.
// Petite implémentation sans dépendance ni réseau, d'après la norme ISO/IEC 18004 : mode octets (UTF-8),
// correction d'erreur M (15 %), versions 1 à 20 (jusqu'à 666 octets : bien plus qu'une adresse), masque
// choisi par la pénalité de la norme. Testée dans qr.test.ts (codes correcteurs, format, version, relecture).

export type Ecc = 'L' | 'M' | 'Q' | 'H';
const ECC_ORDER: Ecc[] = ['L', 'M', 'Q', 'H'];
/** Les deux bits du niveau dans l'information de format (L = 01, M = 00, Q = 11, H = 10). */
const FORMAT_BITS: Record<Ecc, number> = { L: 1, M: 0, Q: 3, H: 2 };
export const MAX_VERSION = 20;

// Codes correcteurs par bloc et nombre de blocs, par niveau puis par version (index 0 inutilisé).
const ECC_PER_BLOCK: Record<Ecc, number[]> = {
  L: [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28],
  M: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26],
  Q: [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30],
  H: [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28],
};
const BLOCKS: Record<Ecc, number[]> = {
  L: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8],
  M: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16],
  Q: [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20],
  H: [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25],
};

export interface QrCode { version: number; ecc: Ecc; mask: number; size: number; modules: boolean[][] }

// --- Corps de Galois GF(256), polynôme 0x11D --------------------------------------------------------
export function gfMul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/** Le polynôme générateur de degré `degree` (coefficients du plus fort au plus faible, sans le 1 de tête). */
export function rsDivisor(degree: number): number[] {
  const out: number[] = new Array(degree - 1).fill(0);
  out.push(1);
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < out.length; j++) {
      out[j] = gfMul(out[j]!, root);
      if (j + 1 < out.length) out[j]! ^= out[j + 1]!;
    }
    root = gfMul(root, 0x02);
  }
  return out;
}

/** Les codes correcteurs de Reed-Solomon d'un bloc de données. */
export function rsRemainder(data: number[], degree: number): number[] {
  const div = rsDivisor(degree);
  const out: number[] = new Array(degree).fill(0);
  for (const b of data) {
    const factor = b ^ out.shift()!;
    out.push(0);
    div.forEach((c, i) => { out[i]! ^= gfMul(c, factor); });
  }
  return out;
}

// --- Géométrie ---------------------------------------------------------------------------------------
export const sizeOf = (version: number) => version * 4 + 17;

/** Modules de données (et de correction) d'une version : tout sauf les motifs fixes. */
export function rawDataModules(version: number): number {
  let n = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const align = Math.floor(version / 7) + 2;
    n -= (25 * align - 10) * align - 55;
    if (version >= 7) n -= 36;
  }
  return n;
}
/** Les blocs d'une version et d'un niveau : leur nombre et leurs codes correcteurs. */
export const eccInfo = (version: number, ecc: Ecc) => ({ blocks: BLOCKS[ecc][version]!, eccLen: ECC_PER_BLOCK[ecc][version]! });
/** Octets de données (hors correction) d'une version et d'un niveau. */
export const dataCodewords = (version: number, ecc: Ecc) =>
  Math.floor(rawDataModules(version) / 8) - ECC_PER_BLOCK[ecc][version]! * BLOCKS[ecc][version]!;

/** Centres des motifs d'alignement (lignes et colonnes). */
export function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const n = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (n * 2 - 2)) * 2;
  const out = [6];
  for (let pos = sizeOf(version) - 7; out.length < n; pos -= step) out.splice(1, 0, pos);
  return out;
}

/** Les 15 bits de format (niveau, masque), code BCH et masque 0x5412 compris. */
export function formatBits(ecc: Ecc, mask: number): number {
  const data = (FORMAT_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** Les 18 bits de version (versions 7 et plus). */
export function versionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

const bit = (x: number, i: number) => ((x >>> i) & 1) !== 0;

export const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

class Grid {
  readonly size: number;
  readonly mod: boolean[][];
  readonly fn: boolean[][];
  readonly version: number;
  constructor(version: number) {
    this.version = version;
    this.size = sizeOf(version);
    this.mod = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.fn = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }
  set(x: number, y: number, dark: boolean) { this.mod[y]![x] = dark; this.fn[y]![x] = true; }
  finder(cx: number, cy: number) {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
      const d = Math.max(Math.abs(dx), Math.abs(dy)), x = cx + dx, y = cy + dy;
      if (x >= 0 && x < this.size && y >= 0 && y < this.size) this.set(x, y, d !== 2 && d !== 4);
    }
  }
  alignment(cx: number, cy: number) {
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) this.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }
  format(ecc: Ecc, mask: number) {
    const b = formatBits(ecc, mask), s = this.size;
    for (let i = 0; i <= 5; i++) this.set(8, i, bit(b, i));
    this.set(8, 7, bit(b, 6));
    this.set(8, 8, bit(b, 7));
    this.set(7, 8, bit(b, 8));
    for (let i = 9; i < 15; i++) this.set(14 - i, 8, bit(b, i));
    for (let i = 0; i < 8; i++) this.set(s - 1 - i, 8, bit(b, i));
    for (let i = 8; i < 15; i++) this.set(8, s - 15 + i, bit(b, i));
    this.set(8, s - 8, true);   // le module toujours sombre
  }
  functionPatterns(ecc: Ecc) {
    const s = this.size;
    for (let i = 0; i < s; i++) { this.set(6, i, i % 2 === 0); this.set(i, 6, i % 2 === 0); }
    this.finder(3, 3); this.finder(s - 4, 3); this.finder(3, s - 4);
    const al = alignmentPositions(this.version), n = al.length;
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
      this.alignment(al[i]!, al[j]!);
    }
    this.format(ecc, 0);
    if (this.version >= 7) {
      const v = versionBits(this.version);
      for (let i = 0; i < 18; i++) {
        const a = s - 11 + (i % 3), b = Math.floor(i / 3);
        this.set(a, b, bit(v, i)); this.set(b, a, bit(v, i));
      }
    }
  }
  codewords(data: number[]) {
    let i = 0;
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < this.size; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j, up = ((right + 1) & 2) === 0, y = up ? this.size - 1 - vert : vert;
          if (!this.fn[y]![x] && i < data.length * 8) { this.mod[y]![x] = bit(data[i >>> 3]!, 7 - (i & 7)); i++; }
        }
      }
    }
  }
  applyMask(mask: number) {
    const f = MASKS[mask]!;
    for (let y = 0; y < this.size; y++) for (let x = 0; x < this.size; x++) if (!this.fn[y]![x] && f(x, y)) this.mod[y]![x] = !this.mod[y]![x];
  }
}

/** Les modules « fixes » d'une version (motifs, format, version) : pour relire un code. */
export function functionModules(version: number): boolean[][] {
  const g = new Grid(version);
  g.functionPatterns('M');
  return g.fn;
}

/** Pénalité d'un masque (norme : suites, carrés 2 × 2, faux motifs de repérage, équilibre). */
export function penalty(m: boolean[][]): number {
  const size = m.length;
  let score = 0;
  const addHistory = (run: number, h: number[]) => { if (h[0] === 0) run += size; h.pop(); h.unshift(run); };
  const countPatterns = (h: number[]) => {
    const n = h[1]!;
    const core = n > 0 && h[2] === n && h[3] === n * 3 && h[4] === n && h[5] === n;
    return (core && h[0]! >= n * 4 && h[6]! >= n ? 1 : 0) + (core && h[6]! >= n * 4 && h[0]! >= n ? 1 : 0);
  };
  const terminate = (color: boolean, run: number, h: number[]) => {
    if (color) { addHistory(run, h); run = 0; }
    addHistory(run + size, h);
    return countPatterns(h);
  };
  for (const axis of [0, 1]) {
    for (let a = 0; a < size; a++) {
      let color = false, run = 0;
      const h = [0, 0, 0, 0, 0, 0, 0];
      for (let b = 0; b < size; b++) {
        const v = axis === 0 ? m[a]![b]! : m[b]![a]!;
        if (v === color) { run++; if (run === 5) score += 3; else if (run > 5) score++; }
        else { addHistory(run, h); if (!color) score += countPatterns(h) * 40; color = v; run = 1; }
      }
      score += terminate(color, run, h) * 40;
    }
  }
  for (let y = 0; y < size - 1; y++) for (let x = 0; x < size - 1; x++) {
    const c = m[y]![x];
    if (c === m[y]![x + 1] && c === m[y + 1]![x] && c === m[y + 1]![x + 1]) score += 3;
  }
  let dark = 0;
  for (const row of m) for (const v of row) if (v) dark++;
  const total = size * size;
  score += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
  return score;
}

/** Les octets de données (mode octets) avec indicateur, longueur, terminaison et remplissage. */
export function dataBytes(bytes: Uint8Array, version: number, ecc: Ecc): number[] {
  const capacity = dataCodewords(version, ecc) * 8;
  const bits: number[] = [];
  const push = (v: number, n: number) => { for (let i = n - 1; i >= 0; i--) bits.push((v >>> i) & 1); };
  push(0b0100, 4);
  push(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) push(b, 8);
  if (bits.length > capacity) throw new Error('trop long');
  push(0, Math.min(4, capacity - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  const out: number[] = [];
  for (let i = 0; i < bits.length; i += 8) out.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  for (let pad = 0xec; out.length < capacity / 8; pad ^= 0xec ^ 0x11) out.push(pad);
  return out;
}

/** Découpe en blocs, ajoute la correction de chacun et entrelace (ordre de pose dans la grille). */
export function interleave(data: number[], version: number, ecc: Ecc): number[] {
  const nBlocks = BLOCKS[ecc][version]!, eccLen = ECC_PER_BLOCK[ecc][version]!;
  const raw = Math.floor(rawDataModules(version) / 8);
  const nShort = nBlocks - (raw % nBlocks), shortLen = Math.floor(raw / nBlocks);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < nBlocks; i++) {
    const dat = data.slice(k, k + shortLen - eccLen + (i < nShort ? 0 : 1));
    k += dat.length;
    const rs = rsRemainder(dat, eccLen);
    if (i < nShort) dat.push(0);
    blocks.push(dat.concat(rs));
  }
  const out: number[] = [];
  for (let i = 0; i < blocks[0]!.length; i++) blocks.forEach((b, j) => { if (i !== shortLen - eccLen || j >= nShort) out.push(b[i]!); });
  return out;
}

/** Le QR code d'un texte (UTF-8), au plus petit format qui le contient. */
export function qrEncode(text: string, ecc: Ecc = 'M'): QrCode {
  const bytes = new TextEncoder().encode(text);
  let version = 1;
  while (version <= MAX_VERSION && 4 + (version <= 9 ? 8 : 16) + bytes.length * 8 > dataCodewords(version, ecc) * 8) version++;
  if (version > MAX_VERSION) throw new Error('texte trop long pour un QR code');
  const g = new Grid(version);
  g.functionPatterns(ecc);
  g.codewords(interleave(dataBytes(bytes, version, ecc), version, ecc));
  let best = 0, bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    g.applyMask(mask);
    g.format(ecc, mask);
    const s = penalty(g.mod);
    if (s < bestScore) { best = mask; bestScore = s; }
    g.applyMask(mask);   // XOR : on revient à la grille sans masque
  }
  g.applyMask(best);
  g.format(ecc, best);
  return { version, ecc, mask: best, size: g.size, modules: g.mod };
}

/** Le tracé SVG des modules sombres (un carré par module, avec la marge blanche). */
export function qrPath(code: QrCode, margin = 4): string {
  let d = '';
  code.modules.forEach((row, y) => {
    let x = 0;
    while (x < code.size) {
      if (!row[x]) { x++; continue; }
      let w = 1;
      while (x + w < code.size && row[x + w]) w++;
      d += `M${x + margin} ${y + margin}h${w}v1h-${w}z`;
      x += w;
    }
  });
  return d;
}

export const ECC_LEVELS = ECC_ORDER;
